import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { ApiService } from './../../api.service';
import { PresenceService } from './../../services/presence.service';
import { CallService } from './../../services/call.service';
import { EchoService } from './../../services/echo.service';
import { environment } from './../../../environments/environment';

interface ConvMeta {
  friend_id: number;
  chat: string;
  mine: boolean;
  created_at: string;
  unread: number;
}

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.css']
})
export class MessagesComponent implements OnInit, OnDestroy {
  @ViewChild('msgBox') msgBox?: ElementRef;

  id = localStorage.getItem('user_id');
  user_id = parseInt(this.id as string, 10);

  chatUsers: any[] = [];
  meta: { [friendId: string]: ConvMeta } = {};   // har friend ka aakhri message + unread
  selectedUser: any = null;
  selectedUserId: any = '';
  chatMessages: any[] = [];
  newMessage = '';
  chatDisplay = false;
  isLoading = false;
  loadMessage = false;
  searchText = '';
  tab: 'all' | 'unread' | 'online' = 'all';
  refreshInterval = 4000;
  refreshSubscription: Subscription | undefined;

  quickReplies = ['Thank you', "I'm Interested", 'Send More Info', "Let's Schedule a Call"];

  private listTimer: any;
  private channel: any;
  private onNewMsg = () => this.loadConversations();   // Echo 'message.new' par

  constructor(
    private apiService: ApiService,
    public presence: PresenceService,
    private callService: CallService,
    private http: HttpClient,
    private echoService: EchoService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.apiService.getContact(this.user_id).subscribe(
      (response: any) => {
        // sirf accepted friends se chat/call ho sakti hai
        this.chatUsers = (response.contacts || []).filter((c: any) => c.status === 'Accepted');
        this.isLoading = false;
      },
      (error) => { console.error('Error fetching users:', error); this.isLoading = false; }
    );

    this.loadConversations();
    this.listTimer = setInterval(() => this.loadConversations(), 15000);   // Echo na chale to bhi theek

    try {
      this.channel = this.echoService.connect().private('user.' + this.id).listen('.message.new', this.onNewMsg);
    } catch (e) { /* realtime nahi to polling chalti rahegi */ }
  }

  /* ---------- unread / last message ---------- */
  loadConversations() {
    this.http.get<any>(`${environment.apiUrl}messages/conversations`, { headers: this.echoService.headers() }).subscribe({
      next: (r) => {
        const m: { [k: string]: ConvMeta } = {};
        (r?.conversations || []).forEach((c: ConvMeta) => (m[String(c.friend_id)] = c));
        this.meta = m;

        // jo chat khuli hai us mein naya message aaya to foran parho + read mark karo
        const open = m[String(this.selectedUserId)];
        if (this.chatDisplay && open && open.unread > 0) { this.fetchMessages(); }
      },
      error: () => { /* list ka preview optional hai */ }
    });
  }

  private markRead(friendId: any) {
    this.http.post(`${environment.apiUrl}messages/${friendId}/read`, {}, { headers: this.echoService.headers() }).subscribe({
      next: () => {
        const m = this.meta[String(friendId)]; if (m) { m.unread = 0; }
        window.dispatchEvent(new Event('msg-count-refresh'));
      },
      error: () => {}
    });
  }

  unreadOf(c: any): number {
    return this.meta[String(c.user.id)]?.unread || 0;
  }

  get unreadChats(): number {
    return this.chatUsers.filter(c => this.unreadOf(c) > 0).length;
  }

  preview(c: any): string {
    const m = this.meta[String(c.user.id)];
    if (m?.chat) { return (m.mine ? 'You: ' : '') + m.chat; }
    return this.sub(c.user) || this.presence.statusText(c.user);
  }

  private lastTs(c: any): number {
    const t = new Date(this.meta[String(c.user.id)]?.created_at || 0).getTime();
    return isNaN(t) ? 0 : t;
  }

  lastTime(c: any): string {
    const raw = this.meta[String(c.user.id)]?.created_at;
    if (!raw) { return ''; }
    const d = new Date(raw);
    if (isNaN(d.getTime())) { return ''; }
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    const y = new Date(now); y.setDate(now.getDate() - 1);
    if (d.toDateString() === y.toDateString()) { return 'Yesterday'; }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  /* ---------- list ---------- */
  photo(u: any): string {
    const p = u?.profile_image;
    if (!p || typeof p !== 'string') return 'assets/images/avatar.png';
    return p.startsWith('data:image') || p.startsWith('http') ? p : 'data:image/jpeg;base64,' + p;
  }

  sub(u: any): string {
    return [u?.job_title, u?.company].filter(Boolean).join(' | ');
  }

  get onlineCount(): number {
    return this.chatUsers.filter(c => this.presence.isOnline(c.user.id)).length;
  }

  get filteredUsers(): any[] {
    const q = this.searchText.trim().toLowerCase();
    return this.chatUsers.filter(c => {
      if (this.tab === 'online' && !this.presence.isOnline(c.user.id)) return false;
      if (this.tab === 'unread' && this.unreadOf(c) === 0) return false;
      const name = ((c.user.firstname || '') + ' ' + (c.user.lastname || '')).toLowerCase();
      return !q || name.includes(q) || this.sub(c.user).toLowerCase().includes(q);
    }).sort((a, b) => this.lastTs(b) - this.lastTs(a));   // naya message upar
  }

  /* ---------- chat ---------- */
  openChat(c: any) {
    this.selectedUser = c.user;
    this.selectedUserId = c.user.id;
    this.chatDisplay = true;
    this.chatMessages = [];
    this.loadMessage = true;

    this.fetchMessages(true);
    this.markRead(c.user.id);
    this.startChatMessageRefresh();
  }

  closeChat() {
    this.chatDisplay = false;
    this.selectedUser = null;
    this.selectedUserId = '';
    this.refreshSubscription?.unsubscribe();
  }

  private sig(list: any[]): string {
    return list.length + ':' + list.filter(x => x.read_at).length;   // naya message ya read-status badla?
  }

  private fetchMessages(first = false) {
    const fid = this.selectedUserId;
    if (!fid) return;

    this.apiService.getMessages(this.id, fid).subscribe(
      (response: any) => {
        if (fid !== this.selectedUserId) return;   // beech mein dusri chat khul gayi
        const list: any[] = response.chat || response.Chat || [];

        if (first) {
          this.chatMessages = list;
          this.loadMessage = false;
          this.scrollToBottom();
        } else if (Array.isArray(list) && this.sig(list) !== this.sig(this.chatMessages)) {
          const grew = list.length > this.chatMessages.length;
          this.chatMessages = list;
          if (grew) { this.scrollToBottom(); }
        }

        // friend ka koi message abhi unread hai to read mark karo
        if (list.some(m => !this.isMine(m) && !m.read_at)) { this.markRead(fid); }
      },
      (error) => { console.error('Error fetching messages:', error); this.loadMessage = false; }
    );
  }

  startChatMessageRefresh() {
    this.refreshSubscription?.unsubscribe();   // purana interval band
    this.refreshSubscription = interval(this.refreshInterval).subscribe(() => this.fetchMessages());
  }

  sendMessage(text?: string) {
    const msg = (text ?? this.newMessage).trim();
    if (!msg || !this.selectedUserId) return;

    // turant screen par dikhao
    const now = new Date().toISOString();
    this.chatMessages = [...this.chatMessages, { user_id: this.id, chat: msg, created_at: now, read_at: null }];
    this.meta[String(this.selectedUserId)] = {
      friend_id: this.selectedUserId, chat: msg, mine: true, created_at: now, unread: 0
    };
    if (text === undefined) this.newMessage = '';
    this.scrollToBottom();

    this.apiService.sendMessage(this.id, this.selectedUserId, msg).subscribe(
      () => {},
      () => alert('Message could not be sent')
    );
  }

  call() {
    if (!this.selectedUserId) return;
    this.callService.invite([this.selectedUserId]).catch(() => alert('Could not start the call'));
  }

  isMine(m: any) { return String(m.user_id) === String(this.id); }

  scrollToBottom() {
    setTimeout(() => {
      const el = this.msgBox?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  ngOnDestroy() {
    this.refreshSubscription?.unsubscribe();
    clearInterval(this.listTimer);
    try { this.channel?.stopListening('.message.new', this.onNewMsg); } catch (e) { /* ignore */ }
  }
}
